import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Guid, Logger, RouterStackService, ServerRootService } from '@se/se.ui.html5';
import { select, Store } from '@ngrx/store';
import { find } from 'lodash-es';
import { combineLatest, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, skip } from 'rxjs/operators';

import * as fromRoot from '../root-store/root.state';
import { fromServerIsoString, convertPropertiesToCamelCase } from '../models/interop';
import { EstensaNotificationAuthenticatedHubService } from './angularjs/estensa-notification-authenticated-hub.service';
import { EstensaNotificationService } from './angularjs/estensa-notification.service';
import { AuthenticationService } from './authentication/authentication.service';
import { seExaminationAssociation, seEcgAlarm, seEcgAlarmWarning, seUS2AIAlert } from '../root-store/root.actions';
import { EstensaBusWrapperService } from './angularjs/estensa-bus-wrapper.service';
import {
    seSearchUpdateItems,
    seSearchRemoveItems,
    seSearch,
    seSearchIfFiltersEq,
} from '../search-page/redux/search-page.actions';
import { fromWorklistNotification, SearchFormType } from '../search-page/search-page-type.enum';
import { RadioSettingsService } from './settings/radio-settings.service';
import {
    AlarmSeverity,
    AssociationNotification,
    EcgNotification,
    MpsNotification,
    Notification,
    NotificationType,
    WorklistNotification,
    isWorklistNotification,
    US2AINotification,
} from './signalr-notifications.models';
import { AuditService, AuditType } from './audit.service';
import { EstensaCollectionService } from './angularjs/estensa-collection.service';
import { selectSignalrConnected } from '../connectivity/connectivity.selectors';

interface EstensaWebNotification {
    correlationId: string;
    date: string;
    notification: Notification;
}

@Injectable({
    providedIn: 'root',
})
export class SignalrNotificationsService {
    private pendingEcgAlarms: { notificationId: number; alarms: EcgNotification[] } | null = null;
    private approvedEcgAlarm: number | null = null;
    private disconnectionAlarm: number | null = null;

    private readonly isSignalrConnected$ = this.store.pipe(
        select(selectSignalrConnected),
        skip(1),
        debounceTime(15000),
        distinctUntilChanged(),
    );

    private readonly areAlarmsEnabled$ = this.radioSettingsService.radioSettingsAsync.pipe(
        map((settings) => settings.ecgSettings.enableAlerts),
    );

    constructor(
        private auditService: AuditService,
        private authenticationService: AuthenticationService,
        private collectionService: EstensaCollectionService,
        private estensaBus: EstensaBusWrapperService,
        private estensaNotification: EstensaNotificationService,
        private http: HttpClient,
        private logger: Logger,
        private notificationHub: EstensaNotificationAuthenticatedHubService,
        private store: Store<fromRoot.RootState>,
        private radioSettingsService: RadioSettingsService,
        private routerStack: RouterStackService,
        private serverRoot: ServerRootService,
        private translate: TranslateService,
        private zone: NgZone,
    ) {}

    init() {
        this.notificationHub.on('notifyAll', (evt) => {
            this.zone.run(() => {
                try {
                    // È necessario convertire il case perché SignalR non utilizza il JsonFormatter di Estensa.Web.Rest.
                    const { notification, correlationId } = convertPropertiesToCamelCase<EstensaWebNotification>(evt);

                    this.logger.info(
                        `Received notification on client.\n\ttype: ${notification.notificationType}`,
                        correlationId,
                    );
                    return this.onNotificationReceived(notification, correlationId);
                } catch (err) {
                    this.logger.error(`Error while processing notification: ${err.message}`, evt?.CorrelationId);
                }
            });
        });

        combineLatest([this.isSignalrConnected$, this.areAlarmsEnabled$]).subscribe(
            ([isSignalrConnected, enableAlerts]) => this.onSignalrConnectionChanged(isSignalrConnected, enableAlerts),
        );
    }

    async getActiveAlarms(ignoreTokenErrors: boolean = false) {
        const notifications = await firstValueFrom(
            this.http.get<EcgNotification[]>(
                this.serverRoot.getServerRoot() + '/ECG/GetActiveAlarms',
                this.serverRoot.buildOptions(null, ignoreTokenErrors),
            ),
        );

        return Promise.all(
            notifications.map((notification) => this.onNotificationReceived(notification, new Guid().toString())),
        );
    }

    private async onNotificationReceived(notification: Notification, correlationId: string) {
        if (!(await this.shouldProcessNotification(notification, correlationId))) {
            return;
        }

        switch (notification.notificationType) {
            case NotificationType.Ecg:
                return this.onEcgNotification(notification, correlationId);
            case NotificationType.Mps:
                return this.onMpsQueueNotification(notification, correlationId);
            case NotificationType.Association:
                return this.onAssociation(notification, correlationId);
            case NotificationType.Worklist:
                return this.onWorklistNotification(notification, correlationId);
            case NotificationType.Sr:
                return this.onUS2AINotification(notification, correlationId);
            default:
                this.logger.error(`Notification discarded (unknown notification type)`, correlationId);
        }
    }

    private onMpsQueueNotification(notification: MpsNotification, _: string) {
        let update: boolean = !notification.delete && !notification.add;
        if (update) {
            this.store.dispatch(
                seSearchUpdateItems({
                    formType: SearchFormType.ON_PRINTING_CD,
                    items: [notification.queue],
                    idPropertyName: 'jobID',
                    highlightRow: true,
                }),
            );
        } else if (notification.delete && !notification.add) {
            this.store.dispatch(
                seSearchRemoveItems({
                    formType: SearchFormType.ON_PRINTING_CD,
                    idList: [notification.id],
                    idPropertyName: 'jobID',
                }),
            );
        } else if (notification.add && !notification.delete) {
            this.store.dispatch(
                seSearch({
                    formType: SearchFormType.ON_PRINTING_CD,
                    highlightRow: true,
                }),
            );
        } else {
            // Notifica stato di errore
        }
    }

    private onEcgNotification(notification: EcgNotification, correlationId: string) {
        switch (notification.severityType) {
            case AlarmSeverity.Alert:
                return this.startEcgAlarm(notification, correlationId);
            case AlarmSeverity.StopAlert:
                return this.stopEcgAlarm(notification, correlationId);
            case AlarmSeverity.TemporizedWarning:
                return this.showWarning(notification, correlationId);
            case AlarmSeverity.ExamApproved:
                return this.showApprovedNotification(notification, correlationId);
            default:
                this.logger.error(
                    `Notification discarded (unknown severity type ${notification.severityType})`,
                    correlationId,
                );
        }
    }

    private onAssociation(notification: AssociationNotification, correlationId: string) {
        this.logger.debug(
            `Received association notification for examination ${notification.idExamination}`,
            correlationId,
        );

        this.store.dispatch(
            seExaminationAssociation({
                idExamination: notification.idExamination,
                associationType: notification.associationType,
            }),
        );
    }

    private goToPatientFolder(_: EcgNotification) {
        const lastRouterStackState = this.routerStack.lastState;
        const currentStateData = lastRouterStackState
            ? {
                  url: lastRouterStackState.to,
                  params: lastRouterStackState.params,
              }
            : null;

        this.estensaBus.notify({
            messageType: 'NG2_ROUTER_NAVIGATION_REQUEST',
            data: {
                state: 'cardiology.search',
                currentState: currentStateData,
                params: {
                    clean: true,
                    search: true,
                    patient: null,
                    cleanComeBackStateHistory: null,
                    ignoreRouteChangeEvent: true,
                },
            },
        });
    }

    private goToPendingEcgAlarms(correlationId: string) {
        if (!this.pendingEcgAlarms) {
            // Potrebbe essere per es. un doppio click.
            return;
        }
        const pending = this.pendingEcgAlarms;
        this.pendingEcgAlarms = null;
        this.logger.info('Alarm accepted', correlationId);

        // TODO: Usare un meccanismo più resiliente.
        // Aggiungi gli ecg alla pagina se si sta già refertando.
        if (this.routerStack.url === '/new/vnext/ecg/reporting-details') {
            for (const alarm of pending.alarms) {
                this.store.dispatch(seEcgAlarm({ alarm, correlationId }));
            }
            this.estensaNotification.clearPersistentNotification(pending.notificationId);
            return;
        }

        this.zone.run(() => {
            this.routerStack
                .go('/new/vnext/ecg/reporting-details', {
                    activationAlarms: pending.alarms,
                    exams: pending.alarms.map((alarm) => ({
                        examinationId: alarm.idExamination,
                        patient: alarm.patient,
                        examinationDate: fromServerIsoString(alarm.examinationDate).toDate(),
                        withEcg: true,
                    })),
                })
                .then((hasNavigated) => {
                    if (hasNavigated) {
                        this.pendingEcgAlarms = null;
                        this.estensaNotification.clearPersistentNotification(pending.notificationId);
                    } else {
                        this.pendingEcgAlarms = pending;
                    }
                });
        });
    }

    private onWorklistNotification(notification: WorklistNotification, correlationId: string) {
        this.logger.debug('Received worklist refresh message', correlationId);

        const actualFormType = fromWorklistNotification(notification.formType);

        if (actualFormType === null) {
            this.logger.debug(`Unknown worklist form type: ${notification.formType}`, correlationId);
        }

        this.store.dispatch(
            seSearchIfFiltersEq({
                formType: actualFormType,
                highlightRow: true,
                showDiff: true,
                filters: {
                    date: notification.eventDate,
                },
            }),
        );
    }

    private onUS2AINotification(notification: US2AINotification, correlationId: string) {
        this.logger.debug('Received US2AI message', correlationId);
        const { idRepository, studyInstanceUid } = notification;
        this.store.dispatch(seUS2AIAlert({ idRepository, studyInstanceUid }));
    }

    private showApprovedNotification(alarm: EcgNotification, correlationId: string) {
        if (this.approvedEcgAlarm !== null) {
            this.logger.info('Exam approved notification is already active', correlationId);
            return;
        }

        this.logger.info('Showing exam approved notification', correlationId);

        this.approvedEcgAlarm = this.estensaNotification.insertPersistentNotification({
            description: this.translate.instant('EcgAlarmNewApprovedReport'),
            onClick: () => {
                this.approvedEcgAlarm = null;
                this.goToPatientFolder(alarm);
            },
            onDismiss: () => {
                this.approvedEcgAlarm = null;
            },
        });
    }

    private showWarning(alarm: EcgNotification, correlationId: string) {
        this.logger.info('Showing temporized warning on client...', correlationId);
        this.auditService
            .storeUserAudit(AuditType.WarningReceived, `Ecg vNext Warning Received (IdExam: ${alarm.idExamination})`)
            .subscribe();

        this.store.dispatch(seEcgAlarmWarning({ alarm, correlationId: correlationId }));
    }

    private startEcgAlarm(alarm: EcgNotification, correlationId: string) {
        this.auditService
            .storeUserAudit(AuditType.AlertReceived, `Ecg vNext Alert Received (IdExam: ${alarm.idExamination})`)
            .subscribe();

        if (find(this.pendingEcgAlarms?.alarms, (a) => a.idExamination == alarm.idExamination)) {
            this.logger.warn(
                `Notification discarded (alarm already active for examination ${alarm.idExamination})`,
                correlationId,
            );
            return;
        }

        this.logger.info('Starting alarm on client', correlationId);

        if (this.pendingEcgAlarms) {
            this.pendingEcgAlarms.alarms.push(alarm);
            this.estensaNotification.editPersistentnotification(this.pendingEcgAlarms.notificationId, {
                description: this.getAlarmNotificationDescription(this.pendingEcgAlarms.alarms.length),
            });
        } else {
            const notificationId = this.estensaNotification.insertPersistentNotification({
                description: this.getAlarmNotificationDescription(1),
                onClick: () => this.goToPendingEcgAlarms(correlationId),
            });
            this.pendingEcgAlarms = {
                notificationId,
                alarms: [alarm],
            };
        }
    }

    private stopEcgAlarm(alarm: EcgNotification, correlationId: string) {
        if (!this.pendingEcgAlarms) {
            return;
        }
        this.logger.info('Stopping alarm on client', correlationId);

        const activeAlarms = this.pendingEcgAlarms.alarms.filter((a) => a.idExamination !== alarm.idExamination);
        if (activeAlarms.length === 0) {
            this.estensaNotification.clearPersistentNotification(this.pendingEcgAlarms.notificationId);
            this.pendingEcgAlarms = null;
        } else {
            this.pendingEcgAlarms.alarms = activeAlarms;
            this.estensaNotification.editPersistentnotification(this.pendingEcgAlarms.notificationId, {
                description: this.getAlarmNotificationDescription(this.pendingEcgAlarms.alarms.length),
            });
        }
    }

    private onSignalrConnectionChanged(isConnected: boolean, enableAlerts: boolean) {
        this.logger.info(
            `SignalrNotificationsService: SignalR: ${isConnected ? 'connected' : 'disconnected'}; ECG alerts: ${
                enableAlerts ? 'enabled' : 'disabled'
            }`,
        );
        if (!enableAlerts) {
            return;
        }

        if (!isConnected && this.disconnectionAlarm === null) {
            this.disconnectionAlarm = this.estensaNotification.insertPersistentNotification({
                description: this.translate.instant('Disconnected'),
                onClick: () => (this.disconnectionAlarm = null),
            });
        } else if (isConnected && this.disconnectionAlarm !== null) {
            this.estensaNotification.clearPersistentNotification(this.disconnectionAlarm);
            this.disconnectionAlarm = null;
        }
    }

    private getAlarmNotificationDescription(numAlarms: number) {
        if (numAlarms > 1) {
            return `${this.translate.instant('EcgAlarmStart')} (${numAlarms})`;
        }

        return this.translate.instant('EcgAlarmStart');
    }

    private async shouldProcessNotification(notification: Notification, correlationId: string) {
        const idUser = this.authenticationService.getUserId();

        // La notifica di worklist include l'utente che ha fatto partire la notifica (che non deve essere notificato),
        //   e le businessUnit a cui notificare.
        if (isWorklistNotification(notification)) {
            if (notification.idUser === idUser) {
                this.logger.debug(`Notification discarded (user has started the notification)`, correlationId);
                return false;
            }

            if (notification.idBusinessUnit) {
                const businessUnits = await firstValueFrom(this.collectionService.get('Html5', 'GetBusinessUnits'));
                if (businessUnits.find((bu) => bu.id === notification.idBusinessUnit) === undefined) {
                    this.logger.debug(
                        `Notification discarded (user does not belong to business unit "${notification.idBusinessUnit}")`,
                        correlationId,
                    );
                    return false;
                }

                return true;
            }
        }

        if (!notification.idUsers.includes(idUser)) {
            this.logger.debug(`Notification discarded (current user id: ${idUser})`, correlationId);

            return false;
        }

        return true;
    }
}
